import React, { createContext, useEffect, useState } from "react";

export type Territory = "nigeria";

export type Environment = "production" | "acceptance";

export type Config = {
  appOutOfDatePollRateSeconds: number;
  auth0: { domain: string; clientId: string; audience?: string };
  clinicalSubscriptionServiceUrl: string;
  environment: Environment;
  federationPath: string;
  freshdesk: { token?: string; host?: string };
  launchdarkly: { key: string; relayUrl?: string };
  reportsManagementServiceUrl: string;
  segment: { key?: string };
  sentry: {
    sampleRate: number;
    tracesSampleRate: { default: number };
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
  };
  territory: Territory;
  rxdb?: { dataExpiryMaxAgeMins?: number };
};

export const ConfigContext = createContext<Config | undefined>(undefined);

/**
 * This is an opinionated component in that if there config has been not been
 * passed, or it has failed to fetch, then it will not render its children.
 *
 * This saves the callers of useConfig from checking if the config is set or not.
 * Tradeoff is appropriate as it does not make sense for the app to run without config.
 */
export const ConfigProvider: FCwC<{
  configServicePath?: string;
  children: React.ReactElement;
  loadingElement: React.ReactElement;
  errorElement: (error: Error) => React.ReactElement;
}> = ({
  children,
  configServicePath = "/config",
  loadingElement = null,
  errorElement,
}) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (config) return;

    async function fetchConfig() {
      try {
        const response = await fetch(configServicePath);
        const json = await response.json();
        setConfig(json);
      } catch (e) {
        setError(e);
      }
    }

    fetchConfig();
  }, [config, configServicePath]);

  if (error) {
    return errorElement(error);
  }

  if (!config) {
    return loadingElement;
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};

export function useConfig() {
  const context = React.useContext(ConfigContext);

  if (context === undefined) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }

  return context;
}
